import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import QRCode from 'qrcode'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Skeleton,
} from '@lead-magnifier/ui'
import {
  ArrowLeft,
  Download,
  Copy,
  Check,
  ExternalLink,
  Settings,
  Users,
  TrendingUp,
  Clock,
} from 'lucide-react'
import { useEvent } from '@/hooks/use-events'
import { useWorkspace } from '@/lib/workspace-context'
import { EventExhibitors } from '@/components/event-exhibitors'

export const Route = createFileRoute('/_authenticated/events/$eventId/')({
  component: EventDetailPage,
})

function EventDetailPage() {
  const { eventId } = Route.useParams()
  const { data: event, isLoading, error } = useEvent(eventId)
  const { isPersonal } = useWorkspace()
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [copied, setCopied] = useState(false)

  // Get tenant slug from the event's tenant
  const tenantSlug = (event?.tenants as { slug: string })?.slug || ''
  const formUrl = tenantSlug && event ? `${window.location.origin}/form/${tenantSlug}/${event.slug}` : ''

  useEffect(() => {
    if (!formUrl || !event) return
    QRCode.toDataURL(formUrl, {
      width: 300,
      margin: 2,
      color: {
        dark: event.primary_color || '#000000',
        light: '#ffffff',
      },
    }).then(setQrDataUrl)
  }, [formUrl, event])

  const downloadQR = async (format: 'png' | 'svg') => {
    if (!event) return
    if (format === 'png') {
      const link = document.createElement('a')
      link.download = `${event.slug}-qr.png`
      link.href = qrDataUrl
      link.click()
    } else {
      const svg = await QRCode.toString(formUrl, { type: 'svg' })
      const blob = new Blob([svg], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = `${event.slug}-qr.svg`
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
    }
  }

  const copyLink = async () => {
    await navigator.clipboard.writeText(formUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardContent className="p-8">
              <Skeleton className="h-[300px] w-[300px] mx-auto" />
            </CardContent>
          </Card>
          <div className="space-y-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="space-y-6">
        <Link to="/events" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zu Events
        </Link>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">Event nicht gefunden.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const leads = event.leads as Array<{ id: string; doi_confirmed: boolean }> || []
  const totalLeads = leads.length
  const confirmedLeads = leads.filter((l) => l.doi_confirmed).length
  const conversionRate = totalLeads > 0 ? Math.round((confirmedLeads / totalLeads) * 100) : 0

  const statusBadge = {
    draft: { label: 'Entwurf', variant: 'outline' as const },
    active: { label: 'Aktiv', variant: 'default' as const },
    ended: { label: 'Beendet', variant: 'secondary' as const },
    archived: { label: 'Archiviert', variant: 'outline' as const },
  }[event.status as string] || { label: event.status, variant: 'outline' as const }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/events" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{event.name}</h1>
              <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {new Date(event.start_date).toLocaleDateString('de-AT')} – {new Date(event.end_date).toLocaleDateString('de-AT')}
            </p>
          </div>
        </div>
        <Link to={`/events/${eventId}/settings`}>
          <Button variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Einstellungen
          </Button>
        </Link>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* QR Code Card — nur für Personal/Standalone */}
        {isPersonal && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>QR-Code</CardTitle>
            <CardDescription>
              Drucke diesen QR-Code aus und platziere ihn an deinem Messestand
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={`QR-Code für ${event.name}`}
                  className="rounded-lg shadow-lg"
                  style={{ width: 300, height: 300 }}
                />
              ) : (
                <Skeleton className="w-[300px] h-[300px] rounded-lg" />
              )}
            </div>

            <div className="flex flex-wrap justify-center gap-2">
              <Button onClick={() => downloadQR('png')}>
                <Download className="h-4 w-4 mr-2" />
                PNG herunterladen
              </Button>
              <Button variant="outline" onClick={() => downloadQR('svg')}>
                <Download className="h-4 w-4 mr-2" />
                SVG herunterladen
              </Button>
              <Button variant="outline" onClick={copyLink}>
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? 'Kopiert!' : 'Link kopieren'}
              </Button>
              <Button variant="ghost" asChild>
                <a href={formUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Formular öffnen
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Stats Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Leads
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalLeads}</div>
              <p className="text-xs text-muted-foreground">
                {confirmedLeads} bestätigt
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Conversion Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{conversionRate}%</div>
              <div className="mt-2 w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${conversionRate}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Laufzeit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm">
                <p>Start: {new Date(event.start_date).toLocaleDateString('de-AT')}</p>
                <p>Ende: {new Date(event.end_date).toLocaleDateString('de-AT')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Aussteller — nur in Organisations-Workspaces */}
      {!isPersonal && (
        <EventExhibitors
          eventId={eventId}
          tenantSlug={tenantSlug}
          eventSlug={event.slug}
        />
      )}

      {/* Recent Leads */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Letzte Leads</CardTitle>
            <CardDescription>Die neuesten erfassten Kontakte</CardDescription>
          </div>
          <Link to={`/events/${eventId}/leads`}>
            <Button variant="outline" size="sm">
              Alle ansehen
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {totalLeads === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Noch keine Leads vorhanden. Teile den QR-Code, um Leads zu sammeln.
            </p>
          ) : (
            <div className="text-sm text-muted-foreground">
              {totalLeads} Leads gesammelt
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
